import { NgClass } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { SAVE_SERVICE } from '@core/saves/saves.interface';
import { ACTIVATION_SERVICE } from '@features/auth/activation.service.interface';
import { HomeProfile, HomeProfileService } from '../home-profile.service';
import { PortalButtonComponent } from '../portal-button/portal-button.component';
import { PortalButtonDirective } from '../portal-button/portal-button.directive';
import { PortalInputComponent } from '../portal-input/portal-input.component';
import { PortalPanelComponent } from '../portal-panel/portal-panel.component';
import { PortalProfileComponent } from '../portal-profile/portal-profile.component';

@Component({
  selector: 'app-portal-home-profiles',
  imports: [
    PortalPanelComponent,
    PortalButtonComponent,
    PortalProfileComponent,
    FormsModule,
    ReactiveFormsModule,
    PortalInputComponent,
    PortalButtonDirective,
    NgClass,
  ],
  templateUrl: './portal-home-profiles.component.html',
  styleUrl: './portal-home-profiles.component.css',
})
export class PortalHomeProfilesComponent {
  homeProfileService = inject(HomeProfileService);
  activationService = inject(ACTIVATION_SERVICE);
  saveService = inject(SAVE_SERVICE);
  router = inject(Router);
  profiles = signal<HomeProfile[]>([]);
  sortedProfiles = computed(() => {
    return this.profiles().sort((a, b) => a.name.localeCompare(b.name));
  });
  showResetConfirmDialog = signal(false);
  showAddProfileDialog = signal(false);

  // Form for adding new profiles
  addProfileForm = new FormGroup({
    profileName: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    playerCode: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
  });

  // Error state signals
  showProfileNameError = signal(false);
  showPlayerCodeError = signal(false);

  constructor() {
    this.loadProfiles();
  }

  loadProfiles() {
    this.profiles.set(this.homeProfileService.getHomeProfiles());
  }

  onAddProfile() {
    if (this.addProfileForm.valid) {
      const profileName = this.addProfileForm.value.profileName!;
      const playerCode = this.addProfileForm.value.playerCode!;

      // Reset error states
      this.showProfileNameError.set(false);
      this.showPlayerCodeError.set(false);

      // Check if profile name already exists
      const existingProfiles = this.homeProfileService.getHomeProfiles();
      if (existingProfiles.some((p) => p.name === profileName)) {
        this.showProfileNameError.set(true);
        return;
      }

      // Check if player code already exists
      const existingProfile = existingProfiles.find((p) => p.playerCode === playerCode);
      if (existingProfile) {
        // Update only the name
        existingProfile.name = profileName;
        this.homeProfileService.removeHomeProfile(playerCode);
        this.homeProfileService.addHomeProfile(existingProfile);
        this.loadProfiles();
        this.closeAddProfileDialog();
        return;
      }

      // Check if you can login with the player code
      this.saveService
        .login(playerCode)
        .then(() => {
          console.log('Login successful for player code:', playerCode);
          this.saveService.logout(); // Only need to check if login is possible, no need to stay logged in
          // Add the new profile
          const newProfile: HomeProfile = {
            name: profileName,
            playerCode: playerCode,
          };

          this.addProfile(newProfile);
          this.closeAddProfileDialog(); // Close dialog and reset form
        })
        .catch((error) => {
          console.error('Login failed for player code:', playerCode, error);
          this.showPlayerCodeError.set(true);
        });
    }
  }

  selectProfile(profile: HomeProfile) {
    console.log('Selected profile:', profile);
    this.saveService
      .login(profile.playerCode)
      .then(() => {
        // Navigate to the game with the selected profile
        this.router.navigate(['/']);
      })
      .catch((error) => {
        console.error('Failed to login with profile:', profile, error);
      });
  }

  addProfile(profile: HomeProfile) {
    this.homeProfileService.addHomeProfile(profile);
    this.loadProfiles();
  }

  removeProfile(playerCode: string) {
    this.homeProfileService.removeHomeProfile(playerCode);
    this.loadProfiles();
  }

  resetProfiles() {
    this.homeProfileService.resetHomeProfiles();
    this.loadProfiles();
  }

  openAddProfileDialog() {
    this.showAddProfileDialog.set(true);
  }

  closeAddProfileDialog() {
    this.showAddProfileDialog.set(false);
    this.showProfileNameError.set(false);
    this.showPlayerCodeError.set(false);
    this.addProfileForm.reset();
  }

  openResetAppDialog() {
    this.showResetConfirmDialog.set(true);
  }

  confirmResetApp() {
    this.homeProfileService.resetHomeProfiles();
    this.activationService.deleteActivationData();
    this.showResetConfirmDialog.set(false);
    this.router.navigate(['/selectDeviceType']);
  }

  cancelResetApp() {
    this.showResetConfirmDialog.set(false);
  }
}
